/** FlightsHotel ldsDealId SSOT — LDS opaque vs HR internal base64 JSON. */

export const HR_INTERNAL_LDS_DEAL_TOKEN_NOT_OPAQUE =
  "HR_INTERNAL_LDS_DEAL_TOKEN_NOT_OPAQUE";

const HR_INTERNAL_JSON_KEYS = new Set([
  "disPckId",
  "disDealId",
  "packageId",
  "loadFileName",
  "supplierId",
  "supplierPackageId",
  "packageSubType",
  "packageSubject",
  "hotelCity",
  "hotelName",
  "totFare",
  "adtFare",
  "infFare",
  "addFee",
  "currency",
  "packageDepartureDate",
  "packageReturnDate",
  "roomTypeId",
  "roomType",
  "dealSubType",
  "flightLegs",
  "roomSet",
  "RoomSet",
]);

const LDS_OPAQUE_PREFIXES = ["vGaA", "FEDm"];
const B64_CHARS = /^[A-Za-z0-9+/=_-]+$/;

const EXPLICIT_COMBO_FORBIDDEN_HR_KEYS = new Set([
  "disPckId",
  "disDealId",
  "totFare",
  "adtFare",
  "chldFare",
  "infFare",
  "packageId",
  "supplierPackageId",
]);

const EXPLICIT_COMBO_REQUIRED_LEG_FIELDS = ["flightNumber", "cityCodeDep", "cityCodeArr"];

function pickStr(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function isComboJsonToken(token) {
  const raw = String(token || "").trim();
  if (!raw) return false;
  if (raw.startsWith("{")) return true;
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") {
      if ("roomSet" in obj && "flightLegs" in obj) return true;
      if ("RoomSet" in obj && "FlightLegs" in obj) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** FE-built per-room combo JSON (not HR package snapshot with disDealId/totFare). */
export function isExplicitRoomFlightComboJson(token) {
  const raw = String(token || "").trim();
  if (!raw.startsWith("{")) return false;
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!obj || typeof obj !== "object") return false;
  if (Object.keys(obj).some((k) => EXPLICIT_COMBO_FORBIDDEN_HR_KEYS.has(k))) return false;
  const roomSet = String(obj.roomSet || obj.RoomSet || obj.room_set || "").trim();
  if (!roomSet) return false;
  const legs = obj.flightLegs ?? obj.FlightLegs;
  if (!Array.isArray(legs) || !legs.length) return false;
  return legs.every(
    (leg) =>
      leg &&
      typeof leg === "object" &&
      EXPLICIT_COMBO_REQUIRED_LEG_FIELDS.every((field) => String(leg[field] || "").trim())
  );
}

/**
 * Historical FlightsHotel room.ldsDealId formats LDS accepted in production SOAP.
 * Parity with BE services/lds_proposal_token_format.is_flights_hotel_room_deal_token_for_soap.
 */
export function isFlightsHotelRoomDealTokenForSoap(token) {
  const raw = String(token || "").trim();
  if (!raw) return false;
  if (isExplicitRoomFlightComboJson(raw)) return true;
  if (raw.startsWith("{") && isComboJsonToken(raw)) return true;
  if (raw.startsWith("eyJ") && isHrInternalBase64JsonDealToken(raw)) return true;
  if (/^\d{8,}$/.test(raw)) return true;
  if (isLdsOpaqueDealToken(raw)) return true;
  return false;
}

/** @param {unknown} token */
export function parseHrInternalDealTokenObject(token) {
  const raw = String(token || "").trim();
  if (!raw) return null;
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw);
      return obj && typeof obj === "object" ? obj : null;
    } catch {
      return null;
    }
  }
  try {
    let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = typeof atob === "function" ? atob(b64) : "";
    if (!bin) return null;
    const obj = JSON.parse(bin);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}

/** HR-internal base64 JSON (eyJ…) or combo JSON — not LDS opaque. */
export function isHrInternalBase64JsonDealToken(token) {
  const raw = String(token || "").trim();
  if (!raw) return false;
  if (isExplicitRoomFlightComboJson(raw)) return false;
  if (isComboJsonToken(raw)) return true;
  const decoded = parseHrInternalDealTokenObject(raw);
  if (!decoded || typeof decoded !== "object") return false;
  const keys = Object.keys(decoded);
  if (keys.some((k) => HR_INTERNAL_JSON_KEYS.has(k))) return true;
  if ("roomSet" in decoded && "flightLegs" in decoded) return true;
  if (raw.startsWith("eyJ") && keys.length > 0) return true;
  return false;
}

/** True for LDS opaque tokens (FEDm, vGaA, long base64); false for HR eyJ/combo. */
export function isLdsOpaqueDealToken(token) {
  const raw = String(token || "").trim();
  if (!raw) return false;
  if (isComboJsonToken(raw)) return false;
  if (isHrInternalBase64JsonDealToken(raw)) return false;
  if (LDS_OPAQUE_PREFIXES.some((prefix) => raw.startsWith(prefix))) return true;
  if (raw.startsWith("eyJ")) return false;
  if (raw.length >= 64 && B64_CHARS.test(raw)) return true;
  if (/^\d{8,}$/.test(raw)) return false;
  return raw.length >= 32 && B64_CHARS.test(raw);
}

const LDS_TOKEN_FIELDS = ["ldsDealId", "LdsDealId", "dealToken"];
const HR_SNAPSHOT_FIELDS = ["dealId", "DealId", "deal_id"];

/**
 * Never overwrite LDS opaque ldsDealId with HR JSON. Move HR payload to hrDealSnapshot.
 * @param {object} row
 * @returns {object}
 */
export function preserveLdsDealIdOnExtendedRow(row) {
  if (!row || typeof row !== "object") return row;
  const out = { ...row };

  let opaque = "";
  for (const key of LDS_TOKEN_FIELDS) {
    const v = pickStr(out, [key]);
    if (v && isLdsOpaqueDealToken(v)) {
      opaque = v;
      break;
    }
  }

  const hrTokens = [];
  for (const key of [...LDS_TOKEN_FIELDS, ...HR_SNAPSHOT_FIELDS]) {
    const v = pickStr(row, [key]);
    if (v && isHrInternalBase64JsonDealToken(v)) {
      hrTokens.push({ key, token: v });
    }
  }

  if (opaque) {
    out.ldsDealId = opaque;
    if (out.LdsDealId != null) out.LdsDealId = opaque;
    for (const { token } of hrTokens) {
      if (token !== opaque) {
        if (!out.hrDealSnapshot) out.hrDealSnapshot = token;
        if (!out.internalDealKey) out.internalDealKey = token;
      }
    }
    return out;
  }

  const ldsField = pickStr(out, LDS_TOKEN_FIELDS);
  if (ldsField && isHrInternalBase64JsonDealToken(ldsField)) {
    if (!out.hrDealSnapshot) out.hrDealSnapshot = ldsField;
    if (!out.internalDealKey) out.internalDealKey = ldsField;
    delete out.ldsDealId;
    delete out.LdsDealId;
  }

  for (const { key, token } of hrTokens) {
    if (HR_SNAPSHOT_FIELDS.includes(key)) {
      if (!out.hrDealSnapshot) out.hrDealSnapshot = token;
      if (!out.internalDealKey) out.internalDealKey = token;
    }
  }

  return out;
}

/**
 * True when FE should block room.ldsDealId before save (BE create-guard parity).
 */
export function shouldBlockFlightsHotelRoomLdsDealIdToken(token) {
  const tok = String(token ?? "").trim();
  if (!tok) return false;
  if (isFlightsHotelRoomDealTokenForSoap(tok)) return false;
  return isHrInternalBase64JsonDealToken(tok);
}

/**
 * Block FlightsHotel order save when room.ldsDealId is HR JSON not allowed for SOAP.
 * Allow-first parity with BE assert_flights_hotel_room_deal_tokens_on_order_create.
 * @param {unknown} roomsAndPaxInfo
 */
export function assertFlightsHotelRoomLdsDealIdsNotHrJson(roomsAndPaxInfo) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  for (let i = 0; i < rooms.length; i += 1) {
    const slot = rooms[i];
    const inner = slot?.room && typeof slot.room === "object" ? slot.room : slot;
    const tok = pickStr(inner, ["ldsDealId", "LdsDealId"]);
    if (!tok) continue;
    if (!shouldBlockFlightsHotelRoomLdsDealIdToken(tok)) continue;
    const err = new Error(
      `FlightsHotel room[${i}] ldsDealId is HR internal JSON — use LDS opaque token`
    );
    err.code = HR_INTERNAL_LDS_DEAL_TOKEN_NOT_OPAQUE;
    err.roomIndex = i;
    throw err;
  }
}

/** Prefer first LDS opaque token from candidates. */
export function pickLdsOpaqueDealToken(...candidates) {
  for (const value of candidates) {
    const raw = String(value ?? "").trim();
    if (raw && isLdsOpaqueDealToken(raw)) return raw;
  }
  return null;
}

function decodeDisDealIdFromToken(token) {
  const decoded = parseHrInternalDealTokenObject(token);
  if (!decoded || typeof decoded !== "object") return "";
  return pickStr(decoded, ["disDealId", "DisDealId", "dealId", "deal_id"]);
}

/**
 * Bookable SOAP token for a selected extendedDeal row.
 * Opaque LDS tokens win; otherwise hrDealSnapshot/eyJ after preserveLdsDealIdOnExtendedRow.
 */
export function resolveBookableLdsDealIdFromExtendedRoom(row) {
  if (!row || typeof row !== "object") return "";
  for (const key of ["ldsDealId", "LdsDealId"]) {
    const tok = pickStr(row, [key]);
    if (tok && isLdsOpaqueDealToken(tok)) return tok;
  }
  for (const key of ["ldsDealId", "LdsDealId", "hrDealSnapshot", "internalDealKey", "dealToken"]) {
    const tok = pickStr(row, [key]);
    if (tok && tok.startsWith("eyJ") && isFlightsHotelRoomDealTokenForSoap(tok)) return tok;
  }
  for (const key of [
    "ldsDealId",
    "LdsDealId",
    "hrDealSnapshot",
    "internalDealKey",
    "dealToken",
    "dealId",
    "DealId",
  ]) {
    const tok = pickStr(row, [key]);
    if (tok && isFlightsHotelRoomDealTokenForSoap(tok)) return tok;
  }
  return "";
}

export function findUniqueExtendedRowForSelectedRoom(extendedRows, room) {
  const rows = Array.isArray(extendedRows)
    ? extendedRows.filter((row) => row && typeof row === "object")
    : [];
  if (!rows.length || !room || typeof room !== "object") return null;
  const seq = room["@seqNum"] ?? room.seqNum ?? room.roomId ?? room.RoomId;
  if (seq != null && String(seq).trim() !== "") {
    const seqMatches = rows.filter((row) => {
      const rowSeq = row["@seqNum"] ?? row.seqNum ?? row.roomId ?? row.RoomId;
      return rowSeq != null && String(rowSeq).trim() === String(seq).trim();
    });
    if (seqMatches.length === 1) return seqMatches[0];
    if (seqMatches.length > 1) return null;
  }
  const roomType = pickStr(room, ["roomType", "RoomType", "roomTypeName", "roomName"]);
  if (!roomType) return null;
  const typeMatches = rows.filter(
    (row) =>
      pickStr(row, ["roomType", "RoomType", "roomTypeName", "roomName"]).toUpperCase() ===
      roomType.toUpperCase()
  );
  return typeMatches.length === 1 ? typeMatches[0] : null;
}

/**
 * Single-room FlightsHotel: stamp the selected extended row token onto room.ldsDealId
 * when the stored token is a leftover search-card product.
 */
export function stampSelectedRoomBookableLdsDealIds(roomsAndPaxInfo, extendedRows) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  if (rooms.length !== 1) return rooms;
  return rooms.map((slot) => {
    if (!slot || typeof slot !== "object") return slot;
    const inner = slot.room && typeof slot.room === "object" ? slot.room : slot;
    const current = pickStr(inner, ["ldsDealId", "LdsDealId"]);
    if (current && isExplicitRoomFlightComboJson(current)) return slot;
    if (current && isLdsOpaqueDealToken(current)) return slot;
    const matched = findUniqueExtendedRowForSelectedRoom(extendedRows, inner);
    if (!matched) return slot;
    const selectedType = pickStr(inner, ["roomType", "RoomType", "roomTypeName", "roomName"]);
    const rowType = pickStr(matched, ["roomType", "RoomType", "roomTypeName", "roomName"]);
    if (
      selectedType &&
      rowType &&
      selectedType.toUpperCase() !== rowType.toUpperCase()
    ) {
      return slot;
    }
    const canonical = resolveBookableLdsDealIdFromExtendedRoom(matched);
    if (!canonical) return slot;
    const currentDis = decodeDisDealIdFromToken(current);
    const canonicalDis = decodeDisDealIdFromToken(canonical);
    if (current && currentDis && canonicalDis && currentDis === canonicalDis) return slot;
    if (current === canonical) return slot;
    const nextRoom = {
      ...(slot.room && typeof slot.room === "object" ? slot.room : inner),
      ldsDealId: canonical,
    };
    return { ...slot, room: nextRoom };
  });
}
